import { UwConfigOptions, UwConfigType, UwConfigValue } from "./UwConfigEndpoints";

export type FieldKind = "text" | "number" | "integer" | "boolean" | "integerList";

/** The form's current text for each field, which some fields' choices depend on (e.g. variables by section) */
export type FormTexts = Record<string, string>;

export type FieldDef = {
  name: string;
  label: string;
  kind: FieldKind;
  /** A value is required (otherwise blank saves as null) */
  required?: boolean;
  /** Text shown for a null value, e.g. "default" for the all-metros value */
  nullLabel?: string;
  /** Keep the column in view when the table scrolls horizontally (for fields that identify a row) */
  pinned?: boolean;
  /** Known values to pick from, given the config's options (from the API) and the form's current values */
  choices?: (options: UwConfigOptions, texts: FormTexts) => string[];
  /** Also allow typing a value that isn't one of the `choices`, e.g. a new metro */
  allowNew?: boolean;
  /** Computed from the other fields rather than entered, e.g. an amenity adjustment's name from its key */
  derive?: (options: UwConfigOptions, texts: FormTexts) => string;
  /** Not editable in the form (set by the system) */
  readOnly?: boolean;
  /** Why the field can't be set right now; it's saved as null (its `nullLabel`) while disabled */
  disabledReason?: (options: UwConfigOptions, texts: FormTexts) => string | undefined;
};

export type UwConfigTypeDef = {
  type: UwConfigType;
  label: string;
  description: string;
  fields: FieldDef[];
  /** Default values for fields that aren't in the form */
  createDefaults?: Record<string, UwConfigValue>;
};

const metroChoices = (options: UwConfigOptions) => options.metro ?? [];

const readyPlanFields: FieldDef[] = [
  { name: "metro", label: "Metro", kind: "text", required: true, pinned: true, choices: metroChoices, allowNew: true },
  { name: "ready_plan_id", label: "Ready Plan", kind: "text", required: true, pinned: true },
  {
    name: "sub_ready_plan_id",
    label: "Sub Ready Plan",
    kind: "text",
    required: true,
    choices: (o) => o.sub_ready_plan_id ?? [],
    allowNew: true,
  },
  {
    name: "property_type",
    label: "Property Type",
    kind: "text",
    required: true,
    choices: (o) => o.property_type ?? [],
    allowNew: true,
  },
  { name: "hard_costs_psf", label: "Hard Costs PSF", kind: "number", required: true },
  { name: "is_adu", label: "Is ADU", kind: "boolean", required: true },
  { name: "min_sellable_sqft", label: "Min Sellable Sqft", kind: "integer" },
  { name: "max_sellable_sqft", label: "Max Sellable Sqft", kind: "integer" },
  { name: "bed", label: "Bed", kind: "integer" },
  { name: "max_bed", label: "Max Bed", kind: "integer" },
  { name: "bath", label: "Bath", kind: "number" },
  { name: "min_lot_size", label: "Min Lot Size", kind: "integer" },
  { name: "lot_size_multiple", label: "Lot Size Multiple", kind: "number" },
  { name: "num_garage_attached", label: "Garage Spaces", kind: "integer" },
  { name: "num_stories", label: "Stories", kind: "integer" },
  { name: "finished_basement_sqft", label: "Finished Basement Sqft", kind: "integer" },
  { name: "copies", label: "Copies", kind: "integer" },
  { name: "required_zip_codes", label: "Required Zip Codes", kind: "integerList" },
];

const costsAndMetricsFields: FieldDef[] = [
  { name: "section", label: "Section", kind: "text", required: true, choices: (o) => o.section ?? [] },
  {
    name: "variable",
    label: "Variable",
    kind: "text",
    required: true,
    choices: (o, texts) => o.variable_by_section?.[texts.section] ?? [],
  },
  { name: "metro", label: "Metro", kind: "text", nullLabel: "default", choices: metroChoices, allowNew: true },
  {
    name: "ready_plan_id",
    label: "Ready Plan",
    kind: "text",
    nullLabel: "default",
    choices: (o, texts) => o.ready_plan_id_by_metro?.[texts.metro] ?? [],
    allowNew: true,
    disabledReason: (o, texts) =>
      !(o.plan_level_sections ?? []).includes(texts.section)
        ? "Only soft costs and investor margin can be set per ready plan"
        : !texts.metro || texts.metro === "default"
          ? "Pick a metro first"
          : undefined,
  },
  { name: "value", label: "Value", kind: "number", required: true },
];

const amenityAdjustmentFields: FieldDef[] = [
  {
    name: "adjustment_key",
    label: "Adjustment Key",
    kind: "text",
    required: true,
    choices: (o) => Object.keys(o.adjustment_name_by_key ?? {}).sort(),
  },
  {
    name: "adjustment_name",
    label: "Adjustment Name",
    kind: "text",
    required: true,
    // falls back to the saved name for a key that's no longer offered
    derive: (o, texts) => o.adjustment_name_by_key?.[texts.adjustment_key] ?? texts.adjustment_name ?? "",
  },
  { name: "metro", label: "Metro", kind: "text", required: true, choices: metroChoices, allowNew: true },
  { name: "adjustment_value", label: "Adjustment Value", kind: "number", required: true },
  { name: "source", label: "Source", kind: "text", required: true, readOnly: true },
];

export const UW_CONFIG_TYPES: UwConfigTypeDef[] = [
  {
    type: "ready-plans",
    label: "Ready Plans",
    description: 'Legacy ready plans by metro. Metros without ready plans use the "default" metro\'s plans.',
    fields: readyPlanFields,
  },
  {
    type: "costs-and-metrics",
    label: "Costs & Metrics",
    description:
      "Cost and metric assumptions. Each value has a default, optionally overridden per metro and (for soft costs and investor margin) per ready plan.",
    fields: costsAndMetricsFields,
  },
  {
    type: "amenity-adjustments",
    label: "Amenity Adjustments",
    description: "Amenity adjustment coefficients by metro. Set a value to 0 for no adjustment.",
    fields: amenityAdjustmentFields,
    createDefaults: { adjustment_type: "percentage", source: "manual" },
  },
];

export function uwConfigTypeDef(type: UwConfigType): UwConfigTypeDef {
  return UW_CONFIG_TYPES.find((t) => t.type === type)!;
}

/** Display a stored value as text, e.g. in the table or an input */
export function formatValue(field: FieldDef, value: UwConfigValue | undefined): string {
  if (value === null || value === undefined) return field.nullLabel ?? "";
  if (field.kind === "integerList") return (value as number[]).join(", ");
  if (field.kind === "boolean") return value ? "Yes" : "No";
  return String(value);
}

/**
 * Parse the text from an input back into a stored value. Numbers are parsed from text (rather than a NumberField) so
 * coefficients keep their full precision instead of being rounded for display.
 */
export function parseValue(field: FieldDef, text: string): { value?: UwConfigValue; error?: string } {
  const trimmed = text.trim();
  if (trimmed === "" || (field.nullLabel && trimmed === field.nullLabel)) {
    return field.required ? { error: "Required" } : { value: null };
  }
  switch (field.kind) {
    case "text":
      return { value: trimmed };
    case "number": {
      const n = Number(trimmed);
      return Number.isFinite(n) ? { value: n } : { error: "Must be a number" };
    }
    case "integer": {
      const n = Number(trimmed);
      return Number.isInteger(n) ? { value: n } : { error: "Must be a whole number" };
    }
    case "integerList": {
      const parts = trimmed
        .split(/[\s,]+/)
        .filter(Boolean)
        .map(Number);
      return parts.every(Number.isInteger) ? { value: parts } : { error: "Must be whole numbers separated by commas" };
    }
    case "boolean":
      return { value: trimmed === "true" };
  }
}

export function sameValue(a: UwConfigValue | undefined, b: UwConfigValue | undefined): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}
